import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Label,
} from '@pantry/ui';
import { useDeleteHousehold } from '@pantry/api-client';
import { ExclamationTriangleIcon, TrashIcon } from '@heroicons/react/24/outline';

interface DeleteHouseholdDialogProps {
  householdId: string;
  householdName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

/**
 * Dialog for confirming household deletion.
 * Requires typing the household name to confirm.
 */
export function DeleteHouseholdDialog({
  householdId,
  householdName,
  open,
  onOpenChange,
  onSuccess,
}: DeleteHouseholdDialogProps) {
  const { t } = useLingui();
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const deleteHousehold = useDeleteHousehold();

  const isConfirmed = confirmText === householdName;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!isConfirmed) {
      setError(t`Please type the household name exactly to confirm`);
      return;
    }

    try {
      await deleteHousehold.mutateAsync(householdId);
      handleClose();
      onSuccess();
    } catch (err) {
      const message = err instanceof Error ? err.message : t`Failed to delete household`;
      setError(message);
    }
  };

  const handleClose = () => {
    setConfirmText('');
    setError(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-3 text-destructive">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-destructive/10">
              <ExclamationTriangleIcon className="h-5 w-5 text-destructive" />
            </div>
            <Trans>Delete Household</Trans>
          </DialogTitle>
          <DialogDescription className="pt-2">
            <Trans>
              This action is <span className="font-semibold text-foreground">permanent</span> and
              cannot be undone. All items, storage locations, members, and data will be permanently
              deleted.
            </Trans>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="rounded-xl bg-destructive/5 border border-destructive/20 p-4 space-y-3">
            <p className="text-sm text-muted-foreground">
              <Trans>To confirm deletion, please type the household name:</Trans>
            </p>
            <p className="font-medium text-foreground bg-muted/50 px-3 py-2 rounded-lg text-center">
              {householdName}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-name" className="sr-only">
              <Trans>Confirm household name</Trans>
            </Label>
            <Input
              id="confirm-name"
              type="text"
              placeholder={t`Type household name to confirm`}
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="rounded-xl"
              autoComplete="off"
            />
          </div>

          {error && (
            <div className="rounded-xl bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive animate-in fade-in slide-in-from-top-2">
              {error}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={deleteHousehold.isPending}
              className="rounded-xl"
            >
              <Trans>Cancel</Trans>
            </Button>
            <Button
              type="submit"
              disabled={!isConfirmed || deleteHousehold.isPending}
              className="rounded-xl bg-destructive hover:bg-destructive/90 text-white"
            >
              {deleteHousehold.isPending ? (
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  <Trans>Deleting...</Trans>
                </div>
              ) : (
                <>
                  <TrashIcon className="h-4 w-4 mr-2" />
                  <Trans>Delete Forever</Trans>
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
